import { TILE_SIZE, GRID_WIDTH, GRID_HEIGHT } from "@/data/game-settings"

export interface TileCoord {
  tileX: number
  tileY: number
}

export interface WorldCoord {
  x: number
  y: number
}

/**
 * Convert world coordinates (pixels) to tile coordinates
 */
export function worldToTile(worldX: number, worldY: number): TileCoord {
  return {
    tileX: Math.floor(worldX / TILE_SIZE),
    tileY: Math.floor(worldY / TILE_SIZE),
  }
}

/**
 * Convert tile coordinates to world coordinates (top-left corner of tile)
 */
export function tileToWorld(tileX: number, tileY: number): WorldCoord {
  return {
    x: tileX * TILE_SIZE,
    y: tileY * TILE_SIZE,
  }
}

/**
 * Convert tile coordinates to world coordinates (center of tile)
 */
export function tileToWorldCenter(tileX: number, tileY: number): WorldCoord {
  return {
    x: tileX * TILE_SIZE + TILE_SIZE / 2,
    y: tileY * TILE_SIZE + TILE_SIZE / 2,
  }
}

/**
 * Get all tile coordinates occupied by a multi-tile entity
 */
export function getTilesOccupied(
  tileX: number,
  tileY: number,
  widthInTiles: number,
  heightInTiles: number,
): TileCoord[] {
  const tiles: TileCoord[] = []
  for (let dy = 0; dy < heightInTiles; dy++) {
    for (let dx = 0; dx < widthInTiles; dx++) {
      tiles.push({ tileX: tileX + dx, tileY: tileY + dy })
    }
  }
  return tiles
}

/**
 * Check if tile coordinates are within the grid bounds
 */
export function isValidTile(tileX: number, tileY: number): boolean {
  return tileX >= 0 && tileY >= 0 && tileX < GRID_WIDTH && tileY < GRID_HEIGHT
}

/**
 * Check if a multi-tile entity fits within the grid
 */
export function canFitInGrid(tileX: number, tileY: number, widthInTiles: number, heightInTiles: number): boolean {
  return tileX >= 0 && tileY >= 0 && tileX + widthInTiles <= GRID_WIDTH && tileY + heightInTiles <= GRID_HEIGHT
}

/**
 * Calculate distance between two world positions
 */
export function worldDistance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1
  const dy = y2 - y1
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Check if a world position is within range of another
 */
export function isInRange(x1: number, y1: number, x2: number, y2: number, range: number): boolean {
  const dx = x2 - x1
  const dy = y2 - y1
  return dx * dx + dy * dy <= range * range
}

// Legacy exports for backward compatibility (will be removed later)
export const pixelToTile = (pixelX: number, pixelY: number) => {
  const result = worldToTile(pixelX, pixelY)
  return { x: result.tileX, y: result.tileY }
}

export const tileToPixel = (tileX: number, tileY: number) => tileToWorld(tileX, tileY)

export const tileToCenterPixel = (tileX: number, tileY: number) => tileToWorldCenter(tileX, tileY)

export const isWithinGrid = isValidTile

export const canFitTowerInGrid = canFitInGrid

export const getTilesOccupiedByTower = (tileX: number, tileY: number, tileWidth: number, tileHeight: number) => {
  return getTilesOccupied(tileX, tileY, tileWidth, tileHeight).map((t) => ({
    x: t.tileX,
    y: t.tileY,
  }))
}
